import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { addDocument, getCollection, updateDocument, deleteDocument } from '../../services/firestoreService';
import ManageUnits from './ManageUnits';
import ManageProducts from './ManageProducts';

// Modelo base para ubicaciones de stock (bodegas / farmacias)
// collection: stockLocations
// {
//   name: string,
//   type: 'bodega' | 'farmacia',
//   isCentral: boolean,
//   description?: string,
//   isActive: boolean,
//   createdAt: Timestamp,
//   updatedAt: Timestamp
// }

const ManageSupplies = () => {
  const [locations, setLocations] = useState([]);
  const [name, setName] = useState('');
  const [type, setType] = useState('bodega');
  const [description, setDescription] = useState('');
  const [isCentral, setIsCentral] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [editingLocation, setEditingLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const data = await getCollection('stockLocations');
      setLocations(data);
    } catch (error) {
      console.error('Error fetching stock locations:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar bodegas y farmacias',
        severity: 'error'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setSnackbar({ open: true, message: 'El nombre es obligatorio', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      // Validar única Bodega Central y única Farmacia Central
      if (isCentral) {
        const existingCentral = locations.find(
          (loc) => loc.type === type && loc.isCentral && (!editingLocation || loc.id !== editingLocation.id)
        );
        if (existingCentral) {
          const labelType = type === 'bodega' ? 'Bodega Central' : 'Farmacia Central';
          setSnackbar({
            open: true,
            message: `Ya existe una ${labelType}. No puedes crear otra central del mismo tipo.`,
            severity: 'error'
          });
          setLoading(false);
          return;
        }
      }

      const now = new Date();
      const locationData = {
        name: name.trim(),
        type,
        isCentral,
        description: description.trim(),
        isActive,
        createdAt: editingLocation ? editingLocation.createdAt || now : now,
        updatedAt: now
      };

      if (editingLocation) {
        await updateDocument('stockLocations', editingLocation.id, locationData);
        setSnackbar({
          open: true,
          message: 'Ubicación actualizada exitosamente',
          severity: 'success'
        });
      } else {
        await addDocument('stockLocations', locationData);
        setSnackbar({
          open: true,
          message: 'Ubicación creada exitosamente',
          severity: 'success'
        });
      }

      // Limpiar formulario
      handleCancelEdit();
      fetchLocations();
    } catch (error) {
      console.error('Error saving stock location:', error);
      setSnackbar({
        open: true,
        message: 'Error al guardar la ubicación',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setName(location.name || '');
    setType(location.type || 'bodega');
    setDescription(location.description || '');
    setIsCentral(!!location.isCentral);
    setIsActive(location.isActive !== undefined ? location.isActive : true);
  };

  const handleCancelEdit = () => {
    setEditingLocation(null);
    setName('');
    setType('bodega');
    setDescription('');
    setIsCentral(false);
    setIsActive(true);
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta ubicación?')) return;

    try {
      await deleteDocument('stockLocations', locationId);
      setSnackbar({
        open: true,
        message: 'Ubicación eliminada exitosamente',
        severity: 'success'
      });
      fetchLocations();
    } catch (error) {
      console.error('Error deleting stock location:', error);
      setSnackbar({
        open: true,
        message: 'Error al eliminar la ubicación',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getTypeLabel = (value) => {
    if (value === 'bodega') return 'Bodega';
    if (value === 'farmacia') return 'Farmacia';
    return value;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Insumos y Medicamentos
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Configura aquí las Bodegas y Farmacias donde se almacenarán insumos y medicamentos, incluyendo
        una Bodega Central y una Farmacia Central.
      </Typography>
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 4
              }}>
              <TextField
                fullWidth
                label="Nombre de la Ubicación"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Ej: Bodega Central, Farmacia Hospitalización"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 3
              }}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={type}
                onChange={(e) => setType(e.target.value)}
                required
              >
                <MenuItem value="bodega">Bodega</MenuItem>
                <MenuItem value="farmacia">Farmacia</MenuItem>
              </TextField>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 3
              }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isCentral}
                    onChange={(e) => setIsCentral(e.target.checked)}
                    color="primary"
                  />
                }
                label={type === 'bodega' ? 'Bodega Central' : 'Farmacia Central'}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 2
              }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Activa"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Descripción / Ubicación Física"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={2}
                placeholder="Ej: Piso 1, sector cirugía; Bodega externa, etc."
              />
            </Grid>
            <Grid size={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ mr: 1 }}
              >
                {loading ? 'Guardando...' : editingLocation ? 'Actualizar Ubicación' : 'Crear Ubicación'}
              </Button>
              {editingLocation && (
                <Button variant="outlined" onClick={handleCancelEdit}>
                  Cancelar
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Bodegas y Farmacias configuradas
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Central</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {locations.map((loc) => (
                <TableRow key={loc.id} hover>
                  <TableCell>{loc.name}</TableCell>
                  <TableCell>{getTypeLabel(loc.type)}</TableCell>
                  <TableCell>{loc.isCentral ? 'Sí' : 'No'}</TableCell>
                  <TableCell>{loc.isActive ? 'Activa' : 'Inactiva'}</TableCell>
                  <TableCell>{loc.description || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(loc)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(loc.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {locations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No hay bodegas ni farmacias configuradas todavía.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box sx={{ mt: 6 }}>
        <ManageUnits />
      </Box>
      <Box sx={{ mt: 6 }}>
        <ManageProducts />
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageSupplies;
